import {
  Pipe,
  PipeTransform,
  Inject,
  LOCALE_ID
} from '@angular/core';
import { DecimalPipe, CurrencyPipe, PercentPipe } from '@angular/common';
import { CompactCurrencyPipe } from './compact-currency.pipe';

// ── Types ────────────────────────────────────────────────────────────────
/**
 * Defines the format options for the AccountingPipe.
 * {{ -100 | accounting }}
 * {{ -1234.5 | accounting:'currency':{ currencyCode: 'USD' } }}
 * {{ -0.25 | accounting:'percent' }}
 * {{ -100 | accounting:'number':{ useParentheses: false } }}
 * {{ 0 | accounting:'currency':{ showZero: false } }}
 * {{ 1200 | accounting:'compactCurrency':{ decimals: 1 } }}
 */
export type AccountingFormat = 'number' | 'currency' | 'percent' | 'compactCurrency';

@Pipe({
  name: 'accounting',
  pure: true,
  standalone: false,
})
export class AccountingPipe implements PipeTransform {

  constructor(
    @Inject(LOCALE_ID) private locale: string
  ) {}

  transform(
    value: number | null | undefined,
    format: AccountingFormat = 'number',
    options?: {
      currencyCode?: string;
      digits?: string;
      showZero?: boolean;
      useParentheses?: boolean;
      decimals?: number;
      compactSuffix?: string;
      spaceBeforeSuffix?: boolean;
      compactLocale?: string;
      trimTrailingZeros?: boolean;
    }
  ): string {

    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }

    const {
      currencyCode = 'USD',
      digits = '1.2-2',
      showZero = true,
      useParentheses = true,
      decimals = 2,
      compactSuffix = 'k',
      spaceBeforeSuffix = false,
      compactLocale = this.locale,
      trimTrailingZeros = true
    } = options || {};

    if (value === 0 && !showZero) {
      return '';
    }

    const absValue = Math.abs(value);
    let formatted = '';

    switch (format) {
      case 'currency':
        formatted = new CurrencyPipe(this.locale)
          .transform(absValue, currencyCode, 'symbol', digits) ?? '';
        break;

      case 'percent':
        formatted = new PercentPipe(this.locale)
          .transform(absValue, digits) ?? '';
        break;

      case 'compactCurrency':
        formatted = new CompactCurrencyPipe().transform(
          absValue,
          decimals,
          compactSuffix,
          spaceBeforeSuffix,
          compactLocale,
          trimTrailingZeros
        );
        break;

      default:
        formatted = new DecimalPipe(this.locale)
          .transform(absValue, digits) ?? '';
    }

    if (value < 0) {
      return useParentheses
        ? `(${formatted})`
        : `-${formatted}`;
    }

    return formatted;
  }
}
